import * as faceapi from 'face-api.js';
import { FaceDetection } from 'face-api.js';

/**
 * Check if an input contains multiple faces.
 * @param input - An input compatible with face-api.js, including HTML elements or tensors.
 * @returns A Promise that resolves to true if multiple faces are detected, false otherwise.
 */
export const detectFaces = async (
  input: faceapi.TNetInput
): Promise<{ numOfFaces: number, detections: FaceDetection[] | null }> => {
  try {
    // Load the TinyFaceDetector model if not already loaded
    if (!faceapi.nets.tinyFaceDetector.isLoaded) {
      await faceapi.nets.tinyFaceDetector.loadFromUri('./models');
    }

    // Perform face detection
    const detections = await faceapi.detectAllFaces(
      input,
      new faceapi.TinyFaceDetectorOptions()
    );

    // Return true if more than one face is detected
    return { numOfFaces: detections.length, detections };
  } catch (error) {
    return { numOfFaces: 0, detections: null };
  }
};


/**
 * Get scaled dimensions
 * @param origWidth
 * @param origHeight
 * @param faceWidth
 * @param desiredFace
 * @param faceMax
 * @param maxHeight
 */
export const getScaledDimensions = ({
    origWidth,
    origHeight,
    faceWidth,    // bounding box width of face
    desiredFace = 250,
    faceMax = 300, // threshold above which we must downscale
    maxHeight = 720,
  }: {
  origWidth: number;
  origHeight: number;
  faceWidth: number;
  desiredFace?: number;
  faceMax?: number;
  maxHeight?: number;
}) => {
  // 1) Scale to ensure image height <= maxHeight
  let scale720 = 1;
  if (origHeight > maxHeight) {
    scale720 = maxHeight / origHeight;
  }

  // After that scale, the face will be this big:
  const faceSizeAfter720 = faceWidth * scale720;

  // 2) If face is still too large (> faceMax), compute additional scale
  let scaleFace = 1;
  if (faceSizeAfter720 > faceMax) {
    console.log('yes still too big no?');
    scaleFace = desiredFace / faceSizeAfter720;
    console.log('desiredFace', desiredFace);
    console.log('faceSizeAfter720', faceSizeAfter720);
    console.log('scaleFace', scaleFace);
    // ensures face becomes ~ desiredFace px
  }

  // 3) Multiply them to get total scale factor
  const finalScale = scale720 * scaleFace;

  // 4) Final output dimensions
  const finalWidth = Math.round(origWidth * finalScale);
  const finalHeight = Math.round(origHeight * finalScale);

  // And the final face size if we apply finalScale to original face bounding box
  const finalFaceSize = faceWidth * finalScale;

  return {
    finalWidth,
    finalHeight,
    finalFaceSize,
    scaleApplied: finalScale,
  };
}


interface ScaleAreaBasedInput {
  origWidth: number;    // original image width
  origHeight: number;   // original image height
  faceWidth: number;    // face bounding box width (in original scale)
  faceHeight: number;   // face bounding box height (in original scale)
  minFaceRatio?: number;
  upscaleSize?: number;
}

interface ScaleAreaBasedOutput {
  finalWidth: number;
  finalHeight: number;
  finalFaceSize: number;   // the *width* of the face after scaling
  scaleApplied: number;    // overall scale factor
}

export function getScaledDimensionsAreaBased({
   origWidth,
   origHeight,
   faceWidth,
   faceHeight,
   minFaceRatio = 0.03,   // coworker used 3% as default
   upscaleSize = 512,     // coworker used 512 as the base “face size”
  }: ScaleAreaBasedInput): ScaleAreaBasedOutput {
  // 1) Compute face area vs. total image area
  const faceArea = faceWidth * faceHeight;
  const imageArea = origWidth * origHeight;
  const faceRatio = faceArea / imageArea;

  // 2) If face ratio is below minFaceRatio, do NOT scale (too small).
  //    Return the original (scale factor = 1).
  if (faceRatio < minFaceRatio) {
    return {
      finalWidth: origWidth,
      finalHeight: origHeight,
      finalFaceSize: faceWidth,  // no change
      scaleApplied: 1,
    };
  }

  // 3) Decide how large we want the face in the final image
  //    coworker logic: bump scale if ratio < 2×, 3×, etc.
  let faceScaleSize = upscaleSize; // default 512
  if (faceRatio < minFaceRatio * 2) {
    faceScaleSize = Math.round(upscaleSize * 1.4);
  } else if (faceRatio < minFaceRatio * 3) {
    faceScaleSize = Math.round(upscaleSize * 1.3);
  } else if (faceRatio < minFaceRatio * 4) {
    faceScaleSize = Math.round(upscaleSize * 1.2);
  } else if (faceRatio < minFaceRatio * 5) {
    faceScaleSize = Math.round(upscaleSize * 1.1);
  }

  // 4) Compute scale factor based on how big we want the face to be.
  //    We use the average so we keep aspect ratio stable:
  const scaleFactorWidth = faceScaleSize / faceWidth;
  const scaleFactorHeight = faceScaleSize / faceHeight;
  const scaleApplied = (scaleFactorWidth + scaleFactorHeight) / 2;

  // 5) Compute final image dimensions
  const rawFinalWidth = Math.round(origWidth * scaleApplied);
  const rawFinalHeight = Math.round(origHeight * scaleApplied);

  // Make sure they are even (like coworker did for H.264)
  let finalWidth = rawFinalWidth - (rawFinalWidth % 2);
  let finalHeight = rawFinalHeight - (rawFinalHeight % 2);

  // 6) If we ended up bigger than original, revert to original
  if (finalWidth > origWidth || finalHeight > origHeight) {
    return {
      finalWidth: origWidth,
      finalHeight: origHeight,
      finalFaceSize: faceWidth,  // no change
      scaleApplied: 1,
    };
  }

  // 7) The *face width* after scaling
  const finalFaceSize = faceWidth * scaleApplied;

  return {
    finalWidth,
    finalHeight,
    finalFaceSize,
    scaleApplied,
  };
}
